import { toast } from "react-toastify";
import SDK from "../../Components/SDK";
import Store from "../../Store";
import { ActiveChatAction } from "../../Actions/RecentChatActions";
import { ChatMessageHistoryDataAction, LoadMoreHistoryAction, ResetChatHistoryMessagesAction } from "../../Actions/ChatHistory";
import { callOriginStrMsgAction } from "../../Actions/StarredAction";
import { getActiveConversationChatId, getChatMessageHistoryById, isSingleChat } from "./ChatHelper";
import { getDataFromRoster, getIdFromJid } from "./User";
import { getGroupData } from "./Group";
import * as UnreadManager from "./UnreadManager";

const HIGHLIGHT_CLASS = "animatefinded";
const HIGHLIGHT_DURATION = 3000;
/** Re-centre after these delays, once late layout shifts have settled. */
const REASSERT_DELAYS = [250, 600];
const HISTORY_PAGE_SIZE = 50;
/** A freshly opened chat needs a few frames to render before the target row exists. */
const SCROLL_POLL_INTERVAL = 100;
const SCROLL_POLL_MAX_ATTEMPTS = 25;

const armJumpMode = (fromPage, msgItem) =>
    Store.dispatch(
        callOriginStrMsgAction({
            fromPage,
            callOriginStrMsg: true,
            activeId: msgItem.fromUserId,
            msgId: msgItem.msgId
        })
    );

const disarmJumpMode = (fromPage, fromUserId, msgId) =>
    Store.dispatch(
        callOriginStrMsgAction({
            fromPage,
            callOriginStrMsg: false,
            activeId: fromUserId,
            msgId
        })
    );

/**
 * Gives up on a jump: clears the loader and the scroll guards, and tells the user
 * rather than leaving a click that appears to do nothing.
 */
const abandonJump = (fromPage, msgItem) => {
    Store.dispatch(LoadMoreHistoryAction(false));
    disarmJumpMode(fromPage, msgItem.fromUserId, msgItem.msgId);
    toast.info("This message is no longer available");
};

/** Centres the message in the conversation viewport rather than pinning it to the top. */
const centerInView = (element) => {
    if (!element) return;
    element.scrollIntoView({ block: "center", inline: "nearest" });
};

const highlightAndCenter = (container, fromUserId, msgId, fromPage) => {
    centerInView(container);
    container.classList.add(HIGHLIGHT_CLASS);
    UnreadManager.localClear(fromUserId);
    // Later-arriving messages/media can shift layout after the initial scroll,
    // so re-assert the centered position a couple of times.
    REASSERT_DELAYS.forEach((delay) => setTimeout(() => centerInView(container), delay));
    setTimeout(() => {
        container.classList.remove(HIGHLIGHT_CLASS);
        disarmJumpMode(fromPage, fromUserId, msgId);
    }, HIGHLIGHT_DURATION);
};

/**
 * Waits for the target row to render (a freshly opened chat needs several frames), then
 * centres it and flashes the highlight. Polling avoids the earlier single-timeout that
 * gave up when the message had not painted yet.
 */
export const scrollToMessage = async (msgId = "", fromUserId = "", fromPage = "starPage") => {
    let attempts = 0;
    const poll = () => {
        const container = document?.getElementById(msgId);
        if (container) {
            highlightAndCenter(container, fromUserId, msgId, fromPage);
            return;
        }
        attempts += 1;
        if (attempts >= SCROLL_POLL_MAX_ATTEMPTS) {
            disarmJumpMode(fromPage, fromUserId, msgId);
            return;
        }
        setTimeout(poll, SCROLL_POLL_INTERVAL);
    };
    poll();
};

/**
 * Falls back to a synthetic active-chat payload when the target chat is not in the
 * recent chat list (a global search hit can point at a chat the user has no recent row for).
 */
const buildActiveChatPayload = (msgItem) => {
    const { fromUserId, fromUserJid, chatType } = msgItem;
    const roster = isSingleChat(chatType) ? getDataFromRoster(fromUserId) : getGroupData(fromUserId);
    return {
        recent: { chatType, fromUserId },
        roster: roster || {},
        chatId: fromUserId,
        chatType,
        chatJid: fromUserJid
    };
};

const resolveActiveChatPayload = (msgItem) => {
    const { recentChatData: { rosterData: { recentChatItems = [] } = {} } = {} } = Store.getState() || {};
    const existing = recentChatItems.find((ele) => ele?.recent?.fromUserId === msgItem.fromUserId);
    if (!existing) return buildActiveChatPayload(msgItem);
    existing.chatId = msgItem.fromUserId;
    existing.chatType = msgItem.chatType;
    existing.chatJid = msgItem.fromUserJid;
    return existing;
};

/**
 * Loads a window of history centred on the target message in a single round trip
 * (older + newer in parallel), the way the in-chat search jump does. This replaces the
 * old approach that paginated from the latest message 50 at a time — which made the first
 * jump to an old message hang while it walked the entire history.
 */
const loadWindowAroundMessage = async (msgItem) => {
    const { privacySettingsData } = Store.getState();
    const userSeenReceip = privacySettingsData?.seenReceipt;
    const request = (position) =>
        SDK.getChatMessages({
            toJid: msgItem.fromUserJid,
            position,
            lastMessageId: msgItem.msgId,
            limit: HISTORY_PAGE_SIZE,
            includeMsgObj: true,
            userSeenReceip
        });

    const [backwardRes, forwardRes] = await Promise.all([request("up"), request("down")]);
    for (const res of [backwardRes, forwardRes]) {
        if (res?.statusCode == 200 && msgItem.fromUserId === getIdFromJid(res.userJid || res.groupJid)) {
            res.chatType = msgItem.chatType;
            Store.dispatch(ChatMessageHistoryDataAction(res));
        }
    }
};

/**
 * Opens the conversation a message belongs to, loads the history around it, then scrolls
 * to and highlights it.
 *
 * @param {object} msgItem  { msgId, fromUserId, fromUserJid, chatType }
 * @param {string} fromPage identifies the caller to the conversation scroll guards
 */
export const jumpToMessage = async (msgItem = {}, fromPage = "starPage") => {
    if (!msgItem.msgId || !msgItem.fromUserId) return;

    armJumpMode(fromPage, msgItem);
    const activeChatPayload = resolveActiveChatPayload(msgItem);

    if (activeChatPayload?.roster?.groupId != null && activeChatPayload?.roster?.isAdminBlocked) {
        toast.info("This group is no longer available");
        disarmJumpMode(fromPage, msgItem.fromUserId, msgItem.msgId);
        return;
    }

    const history = getChatMessageHistoryById(msgItem.fromUserId) || [];
    const alreadyLoaded = history.find((ele) => ele.msgId === msgItem.msgId);
    const isActiveChat = getActiveConversationChatId() == msgItem.fromUserId;

    // Already loaded in the open chat — no fetch, just scroll.
    if (alreadyLoaded && isActiveChat) {
        await scrollToMessage(alreadyLoaded.msgId, msgItem.fromUserId, fromPage);
        return;
    }

    Store.dispatch(LoadMoreHistoryAction(true));

    // Open the chat (once). Reset its history so the centred window has no gap from any
    // previously loaded page, which would otherwise confuse the conversation's scrolling.
    if (!isActiveChat) {
        Store.dispatch(ActiveChatAction(activeChatPayload));
        Store.dispatch(ResetChatHistoryMessagesAction({ fromUserId: msgItem.fromUserId }));
    }

    await loadWindowAroundMessage(msgItem);

    const updatedHistory = getChatMessageHistoryById(msgItem.fromUserId) || [];
    const found = updatedHistory.find((ele) => ele.msgId === msgItem.msgId);

    Store.dispatch(LoadMoreHistoryAction(false));

    if (!found) {
        abandonJump(fromPage, msgItem);
        return;
    }

    await scrollToMessage(found.msgId, msgItem.fromUserId, fromPage);
};
